/**
 * ControlSelect - Multi-select control selector
 * Shows title in field, expands to show options on click
 */

import React, { useEffect, useState } from 'react';
import { AtlasFormsClient } from '@atlas-forms/client-js';
import type { FormControl } from '@atlas-forms/types-js';

interface ControlSelectProps {
  formID: number | undefined;
  value: string[];
  onChange: (controlIDs: string[], controls: FormControl[]) => void;
}

export const ControlSelect: React.FC<ControlSelectProps> = ({
  formID,
  value,
  onChange,
}) => {
  const [controls, setControls] = useState<FormControl[]>([]);
  const [loading, setLoading] = useState(false);
  const selectRef = React.useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (!formID) {
      setControls([]);
      return;
    }

    const loadControls = async () => {
      try {
        setLoading(true);
        const schema = await AtlasFormsClient.getInstance().loadFormDefinition(formID);
        setControls(schema?.controls ?? []);
      } catch (err) {
        console.error('Error loading controls:', err);
        setControls([]);
      } finally {
        setLoading(false);
      }
    };

    loadControls();
  }, [formID]);

  const handleChange = (selectedControlIDs: string[]) => {
    // Fetch the full control objects for selected controls
    const selectedControls = selectedControlIDs
      .map(id => controls.find(c => c.id === id))
      .filter((c): c is FormControl => c !== undefined);

    // Call onChange with both IDs and full control objects
    onChange(selectedControlIDs, selectedControls);
  };

  const displayText =
    value.length === 0
      ? (!formID ? 'Select a referred form first' : 'Pick controls from the referred form…')
      : value.length === 1
      ? `${value.length} control selected`
      : `${value.length} controls selected`;

  return (
    <select
      ref={selectRef}
      multiple
      size={1}
      value={value}
      onChange={(e) => handleChange(Array.from(e.target.selectedOptions, (opt) => opt.value))}
      disabled={loading || !formID}
      className="af-properties-tab__select"
      title={displayText}
      style={{
        appearance: 'none',
        cursor: 'pointer',
      }}
    >
      {loading && <option disabled>Loading controls…</option>}
      {!formID && !loading && <option disabled value="">Select a referred form first</option>}
      {controls.map((control) => (
        <option key={control.id} value={control.id}>
          {control.id} — {control.label || '(no label)'}
        </option>
      ))}
      {!loading && controls.length === 0 && formID && (
        <option disabled value="">No controls available</option>
      )}
    </select>
  );
};
